import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Folder, Share2 } from 'lucide-react';
import Spinner from '../../components/ui/Spinner';
import { formatBytes } from '../../utils/format';
import { listSharedFoldersWithMe, listSharedWithMe, sharedKeys } from './sharedAccessApi';

function accessLabel(actions) {
  return actions.includes('file.write')
    ? 'Edit'
    : actions.includes('file.download')
      ? 'View & download'
      : 'View';
}

/**
 * Files & folders shared with me by email — persistent access via a
 * `resource_grants` row, not a one-time link. Counterpart to /shares (links
 * I've created).
 */
export default function SharedWithMePage() {
  const filesQuery = useQuery({
    queryKey: sharedKeys.sharedWithMe(),
    queryFn: listSharedWithMe,
  });
  const foldersQuery = useQuery({
    queryKey: sharedKeys.sharedFoldersWithMe(),
    queryFn: listSharedFoldersWithMe,
  });
  const files = filesQuery.data ?? [];
  const folders = foldersQuery.data ?? [];
  const isLoading = filesQuery.isLoading || foldersQuery.isLoading;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-2">
        <Share2 className="h-5 w-5 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-800">Shared with me</h1>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : files.length === 0 && folders.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">
            No one has shared a document with you yet.
          </div>
        ) : (
          <>
            {folders.map(({ folder, actions }) => (
              <Link
                key={folder.id}
                to={`/shared-folder/${folder.id}`}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-slate-200/50"
              >
                <Folder className="h-5 w-5 shrink-0 text-amber-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">{folder.name}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  {accessLabel(actions)}
                </span>
              </Link>
            ))}
            {files.map(({ file, actions }) => (
              <Link
                key={file.id}
                to={`/shared/${file.id}`}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-slate-200/50"
              >
                <FileText className="h-5 w-5 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">{file.name}</p>
                  <p className="text-xs text-slate-400">{formatBytes(file.sizeBytes)}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  {accessLabel(actions)}
                </span>
              </Link>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
